// src/data/resources/articles/email-provider-integration.ts

export const emailProviderArticles = [
    {
      id: "connecting-gmail",
      categoryId: "email-provider-integration",
      title: "Connecting Gmail to MailMaid",
      content: `
        <h2>Connecting Gmail to MailMaid</h2>
        <p>Integrating your Gmail account with MailMaid allows you to leverage our AI-powered tools to organize, clean, and manage your emails more efficiently. This guide walks you through the connection process and optimization tips.</p>
        
        <h3>Connection Steps</h3>
        <ol>
          <li><strong>Start the connection process:</strong>
            <ul>
              <li>Log in to your MailMaid account</li>
              <li>Go to "Settings" > "Email Accounts"</li>
              <li>Click "Add Email Account"</li>
              <li>Select "Gmail" from the provider options</li>
            </ul>
          </li>
          <li><strong>Authenticate with Google:</strong>
            <ul>
              <li>You'll be redirected to Google's authentication page</li>
              <li>Enter your Gmail email address and password</li>
              <li>If you have two-factor authentication enabled, complete the verification</li>
            </ul>
          </li>
          <li><strong>Grant permissions:</strong>
            <ul>
              <li>Review the permissions MailMaid is requesting</li>
              <li>Click "Allow" to grant access</li>
              <li>Note: MailMaid requires read and modify access to function properly</li>
            </ul>
          </li>
          <li><strong>Complete synchronization:</strong>
            <ul>
              <li>MailMaid will begin syncing with your Gmail account</li>
              <li>The initial sync may take several minutes depending on your inbox size</li>
              <li>You'll receive a notification once the sync is complete</li>
            </ul>
          </li>
        </ol>
        
        <h3>Gmail-Specific Features</h3>
        <p>MailMaid integrates with several Gmail-specific features:</p>
        <ul>
          <li><strong>Labels:</strong> MailMaid maps Gmail labels to categories</li>
          <li><strong>Stars:</strong> Starred emails in Gmail appear in the "Priority" category</li>
          <li><strong>Filters:</strong> Existing Gmail filters can be imported as MailMaid rules</li>
          <li><strong>Google Workspace:</strong> Business Gmail accounts are fully supported</li>
          <li><strong>Multiple inboxes:</strong> All Gmail inboxes can be managed from one MailMaid interface</li>
        </ul>
        
        <h3>Optimizing Gmail Integration</h3>
        <p>For the best experience when using MailMaid with Gmail:</p>
        <ol>
          <li><strong>Enable IMAP access:</strong>
            <ul>
              <li>In Gmail, go to "Settings" > "Forwarding and POP/IMAP"</li>
              <li>Ensure IMAP is enabled</li>
              <li>Save changes</li>
            </ul>
          </li>
          <li><strong>Allow less secure apps (if needed):</strong>
            <ul>
              <li>For some Google accounts, you may need to allow "less secure apps"</li>
              <li>Go to your Google Account > Security > Less secure app access</li>
              <li>Better alternative: Use an app-specific password if available</li>
            </ul>
          </li>
          <li><strong>Set up Gmail app password (for 2FA users):</strong>
            <ul>
              <li>If you use two-factor authentication, create an app password</li>
              <li>Go to your Google Account > Security > App passwords</li>
              <li>Generate a new app password for MailMaid</li>
              <li>Use this password instead of your regular Gmail password</li>
            </ul>
          </li>
        </ol>
        
        <h3>Troubleshooting Gmail Connection</h3>
        <p>Common issues and solutions:</p>
        <ul>
          <li><strong>Authentication failures:</strong>
            <ul>
              <li>Ensure you're using the correct Google account credentials</li>
              <li>Check if your Google account has restrictions from your organization</li>
              <li>Try using an app-specific password</li>
            </ul>
          </li>
          <li><strong>Sync problems:</strong>
            <ul>
              <li>Verify your internet connection is stable</li>
              <li>Check Gmail storage limits (if your account is full, sync may fail)</li>
              <li>Try disconnecting and reconnecting the account</li>
            </ul>
          </li>
          <li><strong>Missing emails:</strong>
            <ul>
              <li>Check if the emails are in a different Gmail category (Primary, Social, etc.)</li>
              <li>Verify archive settings in MailMaid aren't automatically archiving emails</li>
              <li>Ensure sync frequency is set appropriately</li>
            </ul>
          </li>
        </ul>
        
        <h3>Privacy Considerations</h3>
        <p>Important information about how MailMaid interacts with your Gmail account:</p>
        <ul>
          <li>MailMaid uses OAuth 2.0 for secure authentication</li>
          <li>Your Gmail password is never stored on MailMaid servers</li>
          <li>Email content is processed securely and isn't stored permanently</li>
          <li>You can revoke MailMaid's access to your Gmail account at any time</li>
          <li>To revoke access, go to your Google Account > Security > Third-party apps with account access</li>
        </ul>
        
        <p>With your Gmail account successfully connected to MailMaid, you can now take advantage of our smart categorization, cleanup tools, and AI-powered features to transform your email experience.</p>
      `,
      excerpt: "Step-by-step guide to connecting your Gmail account with MailMaid for optimal email management.",
      order: 1
    }
  ];